import {
  BarChart3,
  Calendar,
  ClipboardList,
  Mail,
  MessageSquare,
  Plug,
  Sparkles,
  Target,
} from "lucide-react";

import { PROFILE_SECTION_STEPS } from "@/components/nudges/registry";
import type { CompanyProfileData } from "@/types/businessData";

import type { SetupReminder, SetupReminderContext } from "./types";

// Prompts the user to finish the business profile that powers matching,
// analytics and AI answers. Mirrors the dashboard nudge's completeness check
// (shared PROFILE_SECTION_STEPS) but surfaces it as an invasive modal.
const completeBusinessProfileReminder: SetupReminder = {
  id: "complete-business-profile",
  priority: 40,
  evaluate(ctx: SetupReminderContext) {
    const { businessProfile } = ctx;

    const profile = businessProfile.companyProfile as
      | CompanyProfileData
      | undefined;
    const completedSteps = profile?.completedSteps ?? [];
    const hasLeads = businessProfile.interestedProducts?.includes("leads");

    const applicableSteps = PROFILE_SECTION_STEPS.filter(
      (s) => s !== "leads-config" || hasLeads,
    );
    const remaining = applicableSteps.filter(
      (s) => !completedSteps.includes(s),
    );
    if (remaining.length === 0) return null;

    const done = applicableSteps.length - remaining.length;

    return {
      icon: ClipboardList,
      title: "Slutför er affärsprofil",
      description:
        "Fyll i er affärsprofil så att Graftus kan ge er skarpare matchningar, analyser och AI-svar som är anpassade efter er verksamhet.",
      progress: { done, total: applicableSteps.length },
      benefits: [
        {
          icon: Target,
          text: "Bättre matchningar och rekommendationer baserat på hur ni arbetar.",
        },
        {
          icon: BarChart3,
          text: "Analyser och insikter som utgår från era mål och nyckeltal.",
        },
        {
          icon: Sparkles,
          text: "AI-svar som förstår er verksamhet, era team och er säljprocess.",
        },
      ],
      cta: { label: "Fyll i affärsprofil", to: "/settings/profile" },
    };
  },
};

// Prompts the user to connect their tools (calendar, email, CRM, Slack) when
// nothing is connected yet. Defers while the connected-apps query is loading.
const connectIntegrationsReminder: SetupReminder = {
  id: "connect-integrations",
  priority: 30,
  evaluate(ctx: SetupReminderContext) {
    if (ctx.connectedAppsCount == null) return "pending";
    if (ctx.connectedAppsCount > 0) return null;

    return {
      icon: Plug,
      title: "Koppla dina integrationer",
      description:
        "Anslut dina verktyg så att Graftus kan arbeta med din kalender, mejl och CRM och ge mer relevanta svar.",
      benefits: [
        {
          icon: Calendar,
          text: "Synka kalendern så att Graftus känner till dina möten och kan förbereda dig.",
        },
        {
          icon: Mail,
          text: "Koppla mejlen för rikare sammanhang i briefer och AI-svar.",
        },
        {
          icon: MessageSquare,
          text: "Koppla CRM och Slack så att teamet får full överblick över varje affär.",
        },
      ],
      cta: { label: "Gå till integrationer", to: "/apps" },
    };
  },
};

export const setupReminders: SetupReminder[] = [
  completeBusinessProfileReminder,
  connectIntegrationsReminder,
];
